import { ActionCreator } from 'redux';
import {
    Campaign,
    CampaignChannel,
    CampaignSimpleStats,
    CampaignStatus,
    CampaignTemplate,
    CampaignTypeFilter,
    ChannelChartData,
    InProgressChannelStats,
} from '@dev/ee-api/ee-api-models';
import { cloneDeep, map, reduce } from 'lodash-es';

import { store } from '@shared-store/shared-store';
import { ALPHABET } from '@/services/constants';
import { isNewAbSplit } from '@/components/Campaigns/helperFunctions';
import { ExportAutomationParams, ExportCampaignsParams } from '@/services/ElasticApi';

import {
    CampaignsInterface,
    AbSplitDisplayMode,
    BeginPollingCampaignSimpleStatsAction,
    CampaignInProgressAction,
    CampaignInProgressPollingAction,
    CampaignInProgressSuccessAction,
    CampaignsActionTypes,
    CampaignsByTargetFetchErrorAction,
    CampaignsByTargetFetchStartAction,
    CampaignsByTargetFetchSuccessAction,
    CampaignsByTargetInterface,
    CampaignStatusCounts,
    CampaignTemplateInterface,
    CancelCampaignAction,
    CancelCampaignStartAction,
    CancelCampaignSuccessAction,
    CancelPollingCampaignSimpleStatsAction,
    CancelPollingInProgressCampaignByChannelNameAction,
    ChangeAutoresponderStatusAction,
    ChangeAutoresponderStatusSuccessAction,
    ClearPickedCampaignAction,
    ClearPreviewCampaignAction,
    CloseDeleteCampaignModalAction,
    CopyCampaignsByNameErrorAction,
    CopyCampaignsByNameStartAction,
    CopyCampaignsByNameSuccessAction,
    DeleteCampaignsAction,
    DeleteCampaignsStartAction,
    DeleteCampaignsSuccessAction,
    ExportAutomationContactsAction,
    ExportCampaignsAction,
    GetCampaignByNameAction,
    GetCampaignByNameActionParams,
    GetCampaignByNameSuccessAction,
    GetCampaignClicksAndOpensByNameAction,
    GetCampaignClicksAndOpensByNameActionSuccess,
    GetCampaignReportsSummaryAction,
    GetCampaignReportSummaryActionParams,
    GetCampaignsAction,
    GetCampaignsErrorAction,
    GetCampaignSimpleStatsAction,
    GetCampaignSimpleStatsSuccessAction,
    GetCampaignsPayload,
    GetCampaignsSuccessAction,
    GetLocationStatsByCountryIsoStartAction,
    GetLocationStatsByCountryIsoSuccessAction,
    LoadPreviewTemplatesAction,
    LoadPreviewTemplatesErrorAction,
    LoadPreviewTemplatesSuccessAtion,
    LocationCountryStatsInterface,
    LocationStatsByCountryIsoInterface,
    PaginationInterface,
    PauseAutoresponderAction,
    PickCampaignsAction,
    PickedCampaignInterface,
    PollingCampaignSimpleStatsSuccessAction,
    PreviewCampaignAction,
    ReactivateAutoresponderAction,
    ReportsClicksandopensByChannelnameGetParams,
    SetAbSplitCurrentTemplateAction,
    SetAbSplitDisplayModeAction,
    SetPickedContactEmailAction,
    SetTemplateVersionLabelsAction,
    TemplateDeletedStatusAction,
    TemplateDeletedStatusErrorAction,
    TemplateNameInterface,
    UnpickAllCampaignsAction,
    UpdateCampaignsPaginationAction,
    SetCampaignCreatorOpenAction,
    GetCampaingsNamesAction,
    GetCampaingsNamesErrorAction,
    GetUpdateCampaignsNamesAction,
} from './types';
import { ApplicationState } from '..';

export const CampaignByTargetStart: ActionCreator<CampaignsByTargetFetchStartAction> = (
    params: CampaignsByTargetInterface
): CampaignsByTargetFetchStartAction => ({
    type: CampaignsActionTypes.LOAD_BY_TARGET_START,
    payload: {
        params,
        loading: true,
    },
});

export const CampaignByTargetError: ActionCreator<
    CampaignsByTargetFetchErrorAction
> = (): CampaignsByTargetFetchErrorAction => ({
    type: CampaignsActionTypes.LOAD_BY_TARGET_ERROR,
    payload: {
        loading: false,
    },
});

export const CampaignByTargetSuccess: ActionCreator<CampaignsByTargetFetchSuccessAction> = (
    byTarget: CampaignChannel[],
    statusCount: CampaignStatusCounts
): CampaignsByTargetFetchSuccessAction => ({
    type: CampaignsActionTypes.LOAD_BY_TARGET_SUCCESS,
    payload: {
        byTarget,
        statusCount,
        loading: false,
    },
});

export const SetCampaignCreatorOpen: ActionCreator<SetCampaignCreatorOpenAction> = (
    isCampaignCreatorOpen: boolean,
    contactsList?: string,
    templateName?: string
): SetCampaignCreatorOpenAction => ({
    type: CampaignsActionTypes.SET_CAMPAIGN_CREATOR_OPEN,
    payload: {
        isCampaignCreatorOpen,
        contactsList,
        templateName,
    },
});

export const GetCampaigns: ActionCreator<GetCampaignsAction> = (params?: GetCampaignsPayload): GetCampaignsAction => ({
    type: CampaignsActionTypes.LOAD_ALL,
    payload: params,
});

export const GetCampaignsError: ActionCreator<GetCampaignsErrorAction> = (): GetCampaignsErrorAction => ({
    type: CampaignsActionTypes.LOAD_ALL_ERROR,
});

export const GetCampaignsSuccess: ActionCreator<GetCampaignsSuccessAction> = (
    collection: Campaign[],
    statusCount: CampaignStatusCounts
): GetCampaignsSuccessAction => ({
    type: CampaignsActionTypes.LOAD_ALL_SUCCESS,
    payload: {
        collection,
        statusCount,
    },
});

export const UpdateCampaignsPagination: ActionCreator<UpdateCampaignsPaginationAction> = (
    pagination: PaginationInterface
): UpdateCampaignsPaginationAction => ({
    type: CampaignsActionTypes.UPDATE_PAGINATION,
    payload: {
        pagination,
    },
});

export const GetCampaignsNames = (campaignType: CampaignTypeFilter[]): GetCampaingsNamesAction => ({
    type: CampaignsActionTypes.CAMPAIGNS_NAMES,
    payload: campaignType,
});

export const GetUpdateCampaignsNames = (campaignsNames: string[]): GetUpdateCampaignsNamesAction => ({
    type: CampaignsActionTypes.UPDATE_CAMPAIGNS_NAMES,
    payload: {
        campaignsNames,
    },
});

export const CampaignsNamesError = (): GetCampaingsNamesErrorAction => ({
    type: CampaignsActionTypes.CAMPAIGNS_NAMES_ERROR,
});

export const DeleteCampaigns = (campaignNames: string[], type: CampaignTypeFilter[]): DeleteCampaignsAction => ({
    type: CampaignsActionTypes.OPEN_MODAL_DELETE_CAMPAIGNS,
    payload: {
        campaignNames,
        type,
    },
});

export const DeleteCampaignsSuccess: ActionCreator<
    DeleteCampaignsSuccessAction
> = (): DeleteCampaignsSuccessAction => ({
    type: CampaignsActionTypes.DELETE_CAMPAIGNS_SUCCESS,
});

export const DeleteCampaignsStart: ActionCreator<DeleteCampaignsStartAction> = (
    campaignNames: string[],
    type: CampaignTypeFilter[]
): DeleteCampaignsStartAction => ({
    type: CampaignsActionTypes.DELETE_CAMPAIGNS_START,
    payload: {
        campaignNames,
        type,
    },
});

export const PickCampaigns: ActionCreator<PickCampaignsAction> = (checkedCampaigns: string[]): PickCampaignsAction => ({
    type: CampaignsActionTypes.PICK_CAMPAIGNS,
    payload: {
        checkedCampaigns,
    },
});

export const PollInProgressCampaignByChannelName: ActionCreator<CampaignInProgressPollingAction> = (
    channelName: string
): CampaignInProgressPollingAction => ({
    type: CampaignsActionTypes.POLLING_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME,
    payload: {
        channelName,
        loading: true,
    },
});

export const GetInProgressCampaignByChannelName: ActionCreator<CampaignInProgressAction> = (
    channelName: string
): CampaignInProgressAction => ({
    type: CampaignsActionTypes.GET_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME,
    payload: {
        channelName,
        loading: true,
    },
});

export const CancelPollingInProgressCampaignByChannelName: ActionCreator<
    CancelPollingInProgressCampaignByChannelNameAction
> = (): CancelPollingInProgressCampaignByChannelNameAction => ({
    type: CampaignsActionTypes.CANCEL_POLLING_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME,
    payload: {
        inProgressCampaign: null,
        loading: false,
    },
});

export const GetInProgressCampaignByChannelNameSuccess: ActionCreator<CampaignInProgressSuccessAction> = (
    inProgressCampaign: InProgressChannelStats
): CampaignInProgressSuccessAction => ({
    type: CampaignsActionTypes.GET_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME_SUCCESS,
    payload: {
        inProgressCampaign,
        loading: false,
    },
});

export const UnpickAllCampaigns: ActionCreator<UnpickAllCampaignsAction> = (): UnpickAllCampaignsAction => ({
    type: CampaignsActionTypes.UNPICK_CAMPAIGNS,
});

export const PauseAutoresponder: ActionCreator<PauseAutoresponderAction> = (
    campaign: Campaign
): PauseAutoresponderAction => ({
    type: CampaignsActionTypes.PAUSE_AUTORESPONDER,
    payload: {
        campaign,
    },
});

export const ReactivateAutoresponder: ActionCreator<ReactivateAutoresponderAction> = (
    campaign: Campaign
): ReactivateAutoresponderAction => ({
    type: CampaignsActionTypes.REACTIVATE_AUTORESPONDER,
    payload: {
        campaign,
    },
});

export const ChangeAutoresponderStatus: ActionCreator<ChangeAutoresponderStatusAction> = (
    campaign: Campaign,
    status: CampaignStatus
): ChangeAutoresponderStatusAction => ({
    type: CampaignsActionTypes.CHANGE_AUTORESPONDER_STATUS,
    payload: {
        campaign,
        status,
    },
});

export const ChangeAutoresponderStatusSuccess: ActionCreator<ChangeAutoresponderStatusSuccessAction> = (
    newCampaign: Campaign
): ChangeAutoresponderStatusSuccessAction => {
    const collection: Campaign[] = map(
        store.getState().campaigns.collection,
        (campaign: Campaign): Campaign => (campaign.Name === newCampaign.Name ? newCampaign : campaign)
    );

    return {
        type: CampaignsActionTypes.CHANGE_AUTORESPONDER_STATUS_SUCCESS,
        payload: {
            collection,
        },
    };
};

export const CancelCampaign: ActionCreator<CancelCampaignAction> = (campaign: Campaign): CancelCampaignAction => ({
    type: CampaignsActionTypes.CANCEL_CAMPAIGN,
    payload: {
        campaign,
    },
});

export const CancelCampaignStart: ActionCreator<CancelCampaignStartAction> = (
    campaign: Campaign
): CancelCampaignStartAction => ({
    type: CampaignsActionTypes.CANCEL_CAMPAIGN_START,
    payload: {
        campaign,
    },
});

export const CancelCampaignSuccess: ActionCreator<CancelCampaignSuccessAction> = (
    campaign: Campaign
): CancelCampaignSuccessAction => {
    const newCampaign: Campaign = cloneDeep(campaign);
    newCampaign.Status = CampaignStatus.Cancelled;

    const collection: Campaign[] = map(
        store.getState().campaigns.collection,
        (campaign: Campaign): Campaign => (campaign.Name === newCampaign.Name ? newCampaign : campaign)
    );

    return {
        type: CampaignsActionTypes.CANCEL_CAMPAIGN_SUCCESS,
        payload: {
            collection,
        },
    };
};

export const PreviewCampaign: ActionCreator<PreviewCampaignAction> = (
    campaign: Campaign,
    isWithTestResult: boolean
): PreviewCampaignAction => ({
    type: CampaignsActionTypes.PREVIEW_CAMPAIGN,
    payload: {
        campaign,
        isWithTestResult,
    },
});

export const ClearPreviewCampaign: ActionCreator<ClearPreviewCampaignAction> = (): ClearPreviewCampaignAction => ({
    type: CampaignsActionTypes.PREVIEW_CAMPAIGN_CLEAR,
    payload: {
        campaign: null,
        templates: null,
    },
});

export const LoadPreviewTemplates: ActionCreator<LoadPreviewTemplatesAction> = (
    names: TemplateNameInterface[]
): LoadPreviewTemplatesAction => ({
    type: CampaignsActionTypes.LOAD_PREVIEW_TEMPLATES,
    payload: {
        names,
    },
});

export const LoadPreviewTemplatesSuccess: ActionCreator<LoadPreviewTemplatesSuccessAtion> = (
    templates: CampaignTemplateInterface[]
): LoadPreviewTemplatesSuccessAtion => ({
    type: CampaignsActionTypes.LOAD_PREVIEW_TEMPLATES_SUCCESS,
    payload: {
        templates,
    },
});

export const LoadPreviewTemplatesError: ActionCreator<
    LoadPreviewTemplatesErrorAction
> = (): LoadPreviewTemplatesErrorAction => ({
    type: CampaignsActionTypes.LOAD_PREVIEW_TEMPLATES_ERROR,
});

export const TemplateDeletedStatus: ActionCreator<TemplateDeletedStatusAction> = (): TemplateDeletedStatusAction => ({
    type: CampaignsActionTypes.TEMPLATE_DELETED_STATUS,
});

export const TemplateDeletedStatusError: ActionCreator<
    TemplateDeletedStatusErrorAction
> = (): TemplateDeletedStatusErrorAction => ({
    type: CampaignsActionTypes.TEMPLATE_DELETED_STATUS_ERROR,
});

export const BeginPollingCampaignSimpleStats: ActionCreator<BeginPollingCampaignSimpleStatsAction> = (
    name: string,
    from: string,
    to: string
): BeginPollingCampaignSimpleStatsAction => ({
    type: CampaignsActionTypes.BEGIN_POLLING_CAMPAIGN_SIMPLE_STATS,
    payload: {
        name,
        from,
        to,
    },
});

export const PollingCampaignSimpleStatsSuccess: ActionCreator<PollingCampaignSimpleStatsSuccessAction> = (
    pickedCampaignSimpleStats: CampaignSimpleStats
): PollingCampaignSimpleStatsSuccessAction => ({
    type: CampaignsActionTypes.POLLING_CAMPAIGN_SIMPLE_STATS_SUCCESS,
    payload: pickedCampaignSimpleStats,
});

export const CancelPollingCampaignSimpleStats: ActionCreator<
    CancelPollingCampaignSimpleStatsAction
> = (): CancelPollingCampaignSimpleStatsAction => ({
    type: CampaignsActionTypes.CANCEL_POLLING_CAMPAIGN_SIMPLE_STATS,
});

export const ClearPickedCampaign: ActionCreator<ClearPickedCampaignAction> = (): ClearPickedCampaignAction => ({
    type: CampaignsActionTypes.CLEAR_PICKED_CAMAPAIGN,
});

export const GetCampaignByNameSuccess: ActionCreator<GetCampaignByNameSuccessAction> = (
    pickedCampaign: PickedCampaignInterface
): GetCampaignByNameSuccessAction => ({
    type: CampaignsActionTypes.GET_CAMPAIGN_BY_NAME_SUCCESS,
    payload: {
        pickedCampaign,
    },
});

export const GetCampaignByName: ActionCreator<GetCampaignByNameAction> = (
    params: GetCampaignByNameActionParams
): GetCampaignByNameAction => ({
    type: CampaignsActionTypes.GET_CAMPAIGN_BY_NAME,
    payload: params,
});

export const getCampaignsStore = (state: ApplicationState): CampaignsInterface => state.campaigns;

export const GetLocationStatsByCountryIso = (
    params: LocationStatsByCountryIsoInterface
): GetLocationStatsByCountryIsoStartAction => ({
    type: CampaignsActionTypes.GET_LOCATION_STATS_BY_COUNTRYISO_START,
    payload: params,
});

export const GetLocationStatsByCountryIsoSuccess = (
    params: LocationCountryStatsInterface
): GetLocationStatsByCountryIsoSuccessAction => ({
    type: CampaignsActionTypes.GET_LOCATION_STATS_BY_COUNTRYISO_SUCCESS,
    payload: { params, loading: false },
});

export const GetCampaignReportsSummary: ActionCreator<GetCampaignReportsSummaryAction> = (
    params: GetCampaignReportSummaryActionParams
): GetCampaignReportsSummaryAction => ({
    type: CampaignsActionTypes.GET_CAMPAIGN_REPORTS_SUMMARY,
    payload: params,
});

export const GetCampaignClicksAndOpensByName: ActionCreator<GetCampaignClicksAndOpensByNameAction> = (
    params: ReportsClicksandopensByChannelnameGetParams
): GetCampaignClicksAndOpensByNameAction => ({
    type: CampaignsActionTypes.GET_CAMPAIGN_CLICKS_AND_OPENS_CHART_DATA,
    payload: {
        ...params,
        onlyChildCampaigns: params.onlyChildCampaigns || true,
    },
});

export const GetCampaignClicksAndOpensByNameSuccess: ActionCreator<GetCampaignClicksAndOpensByNameActionSuccess> = (
    clicksAndOpensChartData: ChannelChartData[]
): GetCampaignClicksAndOpensByNameActionSuccess => ({
    type: CampaignsActionTypes.GET_CAMPAIGN_CLICKS_AND_OPENS_CHART_DATA_SUCCESS,
    payload: {
        clicksAndOpensChartData,
    },
});

export const GetCampaignSimpleStats: ActionCreator<GetCampaignSimpleStatsAction> = (
    params: GetCampaignByNameActionParams
) => ({
    type: CampaignsActionTypes.GET_CAMPAIGN_SIMPLE_STATS,
    payload: params,
});

export const GetCampaignSimpleStatsSuccess: ActionCreator<GetCampaignSimpleStatsSuccessAction> = (
    pickedCampaignSimpleStats: CampaignSimpleStats
): GetCampaignSimpleStatsSuccessAction => ({
    type: CampaignsActionTypes.GET_CAMPAIGN_SIMPLE_STATS_SUCCESS,
    payload: pickedCampaignSimpleStats,
});

export const SetTemplateVersionLabels: ActionCreator<SetTemplateVersionLabelsAction> = (
    campaignTemplates: CampaignTemplate[]
): SetTemplateVersionLabelsAction => {
    const isNewAbSplitCampaign = isNewAbSplit(campaignTemplates);

    const templateLabels = reduce(
        isNewAbSplitCampaign ? campaignTemplates.filter((template) => !template.IsWinner) : campaignTemplates,
        (sum: { [key: string]: string }, template: CampaignTemplate, index: number) => ({
            ...sum,
            [template.Name]: ALPHABET[index].toUpperCase(),
        }),
        {}
    );

    return {
        type: CampaignsActionTypes.SET_ABSPLIT_CAMPAIGN_TEMPLATES_LABELS,
        payload: templateLabels,
    };
};

export const ExportCampaigns: ActionCreator<ExportCampaignsAction> = (
    params: ExportCampaignsParams
): ExportCampaignsAction => ({
    type: CampaignsActionTypes.EXPORT_CAMPAIGNS,
    payload: params,
});

export const ExportAutomationContacts: ActionCreator<ExportAutomationContactsAction> = (
    params: ExportAutomationParams
): ExportAutomationContactsAction => ({
    type: CampaignsActionTypes.EXPORT_AUTOMATION_CONTACTS,
    payload: params,
});

export const SetAbSplitDisplayMode: ActionCreator<SetAbSplitDisplayModeAction> = (
    displayMode: AbSplitDisplayMode
): SetAbSplitDisplayModeAction => ({
    type: CampaignsActionTypes.SET_ABSPLIT_DISPLAY_MODE,
    payload: {
        displayMode,
    },
});

export const SetAbSplitCurrentTemplate: ActionCreator<SetAbSplitCurrentTemplateAction> = (
    currentTemplate: string
): SetAbSplitCurrentTemplateAction => ({
    type: CampaignsActionTypes.SET_ABSPLIT_CURRENT_TEMPLATE,
    payload: {
        currentTemplate,
    },
});

export const SetPickedContactEmail: ActionCreator<SetPickedContactEmailAction> = (
    pickedContactEmail?: string
): SetPickedContactEmailAction => ({
    type: CampaignsActionTypes.SET_PICKED_CONTACT_EMAIL,
    payload: {
        pickedContactEmail,
    },
});

export const getPickedCampaign = (store: ApplicationState): PickedCampaignInterface => store?.campaigns.pickedCampaign;

export const CloseDeleteCampaignModal: ActionCreator<
    CloseDeleteCampaignModalAction
> = (): CloseDeleteCampaignModalAction => ({
    type: CampaignsActionTypes.CLOSE_MODAL_DELETE_CAMPAIGNS,
    payload: {
        isOpened: false,
        campaignNames: [],
        type: null,
    },
});

export const CopyCampaignsByNameStart: ActionCreator<CopyCampaignsByNameStartAction> = (
    name
): CopyCampaignsByNameStartAction => ({
    type: CampaignsActionTypes.COPY_CAMPAIGNS_BY_NAME_START,
    payload: {
        name,
    },
});

export const CopyCampaignsByNameSuccess: ActionCreator<CopyCampaignsByNameSuccessAction> = (
    copiedCampaign: Campaign
): CopyCampaignsByNameSuccessAction => ({
    type: CampaignsActionTypes.COPY_CAMPAIGNS_BY_NAME_SUCCESS,
    payload: {
        copiedCampaign,
    },
});

export const CopyCampaignsByNameError: ActionCreator<
    CopyCampaignsByNameErrorAction
> = (): CopyCampaignsByNameErrorAction => ({
    type: CampaignsActionTypes.COPY_CAMPAIGNS_BY_NAME_ERROR,
});

export const getCampaignsNamesStore = (store: ApplicationState): string[] => store?.campaigns?.campaignsNames;
export const getCampaignsListPagination = (store: ApplicationState): PaginationInterface => store?.campaigns.pagination;
export const getCampaignsCounts = (store: ApplicationState): CampaignStatusCounts => store?.campaigns.statusCount;
export const getCampaignByNameStore = (store: ApplicationState): Campaign => store?.campaigns?.pickedCampaign?.campaign;
